import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { openDB } from 'idb';
import { supabase, getCurrentUserId } from '../../lib/supabase';

type Status = 'idle' | 'running' | 'done' | 'error';

type Report = {
  surgeries: { ok: number; fail: number };
  notes: { ok: number; fail: number };
  plans: { ok: number; fail: number };
  cari: { ok: number; fail: number };
};

async function readIndexedDB() {
  const db = await openDB('implant-registry-db', 3);
  const [surgeries, notes, plans] = await Promise.all([
    db.getAll('surgeries'),
    db.getAll('patient_notes'),
    db.getAll('treatment_plans'),
  ]);
  db.close();
  return { surgeries, notes, plans };
}

async function readCariDB() {
  const db = await openDB('cari-db', 1);
  const stores = db.objectStoreNames;
  const txs = stores.contains('transactions') ? await db.getAll('transactions') : [];
  const clinics = stores.contains('clinics') ? await db.getAll('clinics') : [];
  db.close();
  return { txs, clinics };
}

async function batch<T>(
  items: T[],
  fn: (item: T) => Promise<void>
): Promise<{ ok: number; fail: number }> {
  let ok = 0;
  let fail = 0;
  for (const item of items) {
    try {
      await fn(item);
      ok++;
    } catch {
      fail++;
    }
  }
  return { ok, fail };
}

export default function MigrationPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('idle');
  const [log, setLog] = useState<string[]>([]);
  const [report, setReport] = useState<Report | null>(null);

  const addLog = (msg: string) => setLog((prev) => [...prev, msg]);

  const handleMigrate = async () => {
    setStatus('running');
    setLog([]);
    setReport(null);

    try {
      const userId = await getCurrentUserId();
      addLog(`Kullanıcı: ${userId}`);

      // Read IndexedDB
      addLog('IndexedDB okunuyor...');
      const { surgeries, notes, plans } = await readIndexedDB();
      addLog(`  ${surgeries.length} ameliyat, ${notes.length} not, ${plans.length} tedavi planı bulundu.`);

      const { txs, clinics } = await readCariDB();
      addLog(`  ${clinics.length} klinik, ${txs.length} cari işlem bulundu.`);

      // Migrate surgeries
      addLog('Ameliyatlar aktarılıyor...');
      const surgeriesResult = await batch(surgeries, async (s) => {
        await supabase.from('surgeries').upsert({ ...s, user_id: userId });
      });
      addLog(`  Ameliyat: ${surgeriesResult.ok} başarılı, ${surgeriesResult.fail} hatalı`);

      // Migrate notes
      addLog('Hasta notları aktarılıyor...');
      const notesResult = await batch(notes, async (n) => {
        await supabase.from('patient_notes').upsert({ ...n, user_id: userId });
      });
      addLog(`  Not: ${notesResult.ok} başarılı, ${notesResult.fail} hatalı`);

      // Migrate treatment plans
      addLog('Tedavi planları aktarılıyor...');
      const plansResult = await batch(plans, async (p) => {
        await supabase.from('treatment_plans').upsert({ ...p, user_id: userId });
      });
      addLog(`  Plan: ${plansResult.ok} başarılı, ${plansResult.fail} hatalı`);

      // Migrate cari
      addLog('Cari veriler aktarılıyor...');
      const cariClinicResult = await batch(clinics, async (c) => {
        await supabase.from('cari_clinics').upsert({ ...c, user_id: userId });
      });
      const cariTxResult = await batch(txs, async (t) => {
        await supabase.from('cari_transactions').upsert({ ...t, user_id: userId });
      });
      const cariResult = {
        ok: cariClinicResult.ok + cariTxResult.ok,
        fail: cariClinicResult.fail + cariTxResult.fail,
      };
      addLog(`  Cari: ${cariResult.ok} başarılı, ${cariResult.fail} hatalı`);

      setReport({
        surgeries: surgeriesResult,
        notes: notesResult,
        plans: plansResult,
        cari: cariResult,
      });

      addLog('Migration tamamlandı!');
      setStatus('done');
    } catch (err) {
      addLog(`HATA: ${err instanceof Error ? err.message : String(err)}`);
      setStatus('error');
    }
  };

  const totalOk = report ? Object.values(report).reduce((s, r) => s + r.ok, 0) : 0;
  const totalFail = report ? Object.values(report).reduce((s, r) => s + r.fail, 0) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-950 p-6 flex items-start justify-center">
      <div className="w-full max-w-2xl mt-10">
        <div className="bg-slate-800/60 backdrop-blur border border-slate-700/50 rounded-2xl p-6 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
              <i className="ri-database-2-line text-white text-xl"></i>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg">Veri Migrasyonu</h1>
              <p className="text-slate-400 text-sm">IndexedDB → Supabase</p>
            </div>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6">
            <p className="text-amber-300 text-sm flex items-start gap-2">
              <i className="ri-alert-line mt-0.5 flex-shrink-0"></i>
              Bu işlem tarayıcıdaki yerel veritabanını Supabase'e kopyalar. Mevcut bulut verileri silinmez, üzerine yazılır.
            </p>
          </div>

          {status === 'idle' && (
            <button
              onClick={handleMigrate}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-upload-cloud-2-line text-lg"></i>
              Migrasyonu Başlat
            </button>
          )}

          {status === 'running' && (
            <div className="flex items-center justify-center gap-3 py-4 text-teal-400">
              <div className="w-5 h-5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm font-medium">İşlem devam ediyor...</span>
            </div>
          )}

          {log.length > 0 && (
            <div className="mt-4 bg-slate-900/60 rounded-xl p-4 font-mono text-xs text-slate-300 space-y-1 max-h-64 overflow-y-auto">
              {log.map((line, i) => (
                <div key={i} className={line.startsWith('HATA') ? 'text-red-400' : line.includes('tamamlandı') ? 'text-teal-400 font-bold' : ''}>
                  {line}
                </div>
              ))}
            </div>
          )}

          {status === 'done' && report && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Ameliyat', r: report.surgeries, icon: 'ri-surgical-mask-line' },
                  { label: 'Not', r: report.notes, icon: 'ri-file-text-line' },
                  { label: 'Tedavi Planı', r: report.plans, icon: 'ri-list-check-2' },
                  { label: 'Cari', r: report.cari, icon: 'ri-bank-card-line' },
                ].map(({ label, r, icon }) => (
                  <div key={label} className="bg-slate-900/60 rounded-xl p-3 flex items-center gap-3">
                    <i className={`${icon} text-teal-400 text-lg`}></i>
                    <div>
                      <p className="text-white text-sm font-medium">{label}</p>
                      <p className="text-xs text-slate-400">
                        <span className="text-teal-400">{r.ok} OK</span>
                        {r.fail > 0 && <span className="text-red-400 ml-2">{r.fail} hata</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl px-4 py-3 text-center">
                <p className="text-teal-300 font-semibold">{totalOk} kayıt aktarıldı{totalFail > 0 ? `, ${totalFail} hata` : ''}</p>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all cursor-pointer"
              >
                Dashboard'a Git
              </button>
            </div>
          )}

          {status === 'error' && (
            <button
              onClick={handleMigrate}
              className="mt-4 w-full py-3 bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-600 transition cursor-pointer"
            >
              Tekrar Dene
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
